/** Sustained voices: pads, tanpura drone plucks and a soft round bass. */
import { SILENCE, perc } from '../env';
import { mtof, nyquist } from '../util';
import { link, type Synth } from './synth';

export interface PadOpts {
  readonly attack?: number;
  readonly release?: number;
  readonly wave?: PeriodicWave | OscillatorType;
  /** Detune spread of the two oscillators (cents). */
  readonly detune?: number;
  readonly pan?: number;
}

/**
 * One pad note: two detuned oscillators, slow linear attack, held for `dur`, long exponential
 * release. Route it through a shared filtered pad bus for movement.
 */
export function padNote(s: Synth, t: number, midi: number, dur: number, vel: number, o: PadOpts = {}): void {
  const f = mtof(midi);
  const v = s.pool.voice(t, s.route, { pan: o.pan });
  const g = v.gain();
  g.connect(v.out);
  const a = o.attack ?? 1.2;
  const r = o.release ?? 2.2;
  const hold = Math.max(a, dur);
  const peak = 0.045 * vel;
  const p = g.gain;
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + a);
  p.setValueAtTime(peak, t + hold);
  p.exponentialRampToValueAtTime(SILENCE, t + hold + r);
  p.linearRampToValueAtTime(0, t + hold + r + 0.01);
  const end = t + hold + r + 0.02;
  const det = o.detune ?? 7;
  const wave = o.wave ?? 'triangle';
  for (const c of [-det, det]) {
    const osc = v.osc(wave, f * Math.pow(2, c / 1200));
    osc.connect(g);
    v.play(osc, t, end);
  }
}

/**
 * Tanpura-style drone pluck: a buzzing harmonic-rich tone through a resonant low-pass whose
 * cutoff blooms upward then settles — the shimmering "jawari" movement.
 */
export function tanpura(s: Synth, t: number, midi: number, dur: number, vel: number, pan = 0): void {
  const f = mtof(midi);
  const ny = nyquist(s.kit.ctx);
  const v = s.pool.voice(t, s.route, { pan, send: 0.35 });
  // Web Audio low-pass Q is in dB: ~8 dB gives the buzzing formant its shimmer.
  const lp = v.filter('lowpass', f * 4, 8);
  const g = v.gain();
  link(lp, g, v.out);
  const fr = lp.frequency;
  fr.setValueAtTime(Math.min(ny, f * 4), t);
  fr.exponentialRampToValueAtTime(Math.min(ny, f * 14), t + 0.9);
  fr.exponentialRampToValueAtTime(Math.min(ny, f * 6), t + dur);
  const peak = 0.07 * vel;
  const p = g.gain;
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + 0.015);
  p.exponentialRampToValueAtTime(peak * 0.4, t + 0.8);
  p.exponentialRampToValueAtTime(peak * 0.22, t + dur * 0.7);
  p.exponentialRampToValueAtTime(SILENCE, t + dur);
  p.linearRampToValueAtTime(0, t + dur + 0.01);
  for (const r of [1, 1.0012]) {
    const osc = v.osc(s.kit.waves.tanpura, f * r);
    osc.connect(lp);
    v.play(osc, t, t + dur + 0.02);
  }
}

/** Soft round bass: sine + a touch of triangle octave, gentle low-pass. */
export function softBass(s: Synth, t: number, midi: number, dur: number, vel: number): void {
  const f = mtof(midi);
  const v = s.pool.voice(t, s.route, { send: 0.05 });
  const lp = v.filter('lowpass', 520, 0.6);
  lp.connect(v.out);
  const g1 = v.gain();
  const g2 = v.gain();
  const o1 = v.osc('sine', f);
  const o2 = v.osc('triangle', f * 2);
  link(o1, g1, lp);
  link(o2, g2, lp);
  const d = Math.max(0.15, dur);
  const e1 = perc(g1.gain, t, 0.24 * vel, 0.012, d);
  const e2 = perc(g2.gain, t, 0.05 * vel, 0.008, d * 0.6);
  v.play(o1, t, e1);
  v.play(o2, t, e2);
}
